<template>
  <span
    v-show="errorMessage !== null && errorMessage !== undefined"
    role="alert"
    class="help error-msg"
    :data-test-error="name"
    :data-test-base-validation-error="name"
    v-html="errorMessage"
  />
</template>

<script lang="ts">
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ValidationError',
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  props: {
    name: {
      type: String,
      required: true
    },
    overrides: {
      type: Object,
      required: false,
      default() {
        return null;
      }
    },
    errors: {
      type: Object,
      required: false,
      default() {
        return null;
      }
    }
  },
  computed: {
    errorMessage(): string | null {
      let error = this.error;
      let validationRuleName = this.resolveValidationRuleName;
      if (
        validationRuleName !== null &&
        this.overrides !== null &&
        validationRuleName in this.overrides
      )
        return this.overrides[validationRuleName];
      return error;
    },
    resolveValidationRuleName(): string | null {
      // Enhanced ruleMap to include variations of the keywords
      const ruleMap = {
        minimum: 'min_value',
        'skal minimum': 'min_value',
        'max value': 'max_value',
        'skal være et tal på': 'digits',
        'må ikke vælge den samme fil to gange i': 'file_already_selected',
        maximum: 'max_value',
        'maximum allowed value': 'max_value',
        'skal udfyldes': 'required',
        'skal have et gyldigt format': 'regex',
        'er ikke gyldig': 'is'
        // Add more variations as needed
      };

      const error = this.error?.toLowerCase() ?? ''; // Normalize case for comparison
      const normalizedError = error.replace(/\s+/g, ' '); // Normalize multiple spaces to a single space
      for (const [keyword, ruleName] of Object.entries(ruleMap)) {
        if (normalizedError.includes(keyword)) {
          return ruleName;
        }
      }

      return null;
    },
    error(): string | null {
      if (this.errors) {
        if (this.errors === null) return null;
        if (Object.keys(this.errors).length === 0) return null;
        if (this.errors[this.name] === undefined) return null;
        return this.errors[this.name];
      }
      // https://github.com/logaretm/vee-validate/blob/main/packages/vee-validate/src/ErrorMessage.ts
      const message = this.form?.errors.value[this.name];
      return message as string | null;
    }
  }
});
</script>

<style lang="scss" scoped>
@import '../../styles/settings.scss';
@import '../../styles/colors.scss';

[role='alert'] {
  position: relative;
  display: block;
  z-index: 0;
  padding: rem-calc(10 15);
  margin: rem-calc(8 0 20 0);
  line-height: 1.2;
  color: $error-text-new;
  background-color: $background-grey;
  border-radius: rem-calc(15);

  &::before {
    content: '';
    position: absolute;
    display: block;
    top: rem-calc(-24);
    left: rem-calc(30);
    border: rem-calc(12) solid;
    border-color: transparent transparent $background-grey;
  }

  &.is-visible {
    display: block;
  }

  p {
    &:last-child {
      margin-bottom: 0;
    }
  }

  &.warning {
    padding-left: 50px;
    color: $black;

    &::after {
      content: '\3b';
      position: absolute;
      top: 10px;
      left: 20px;
      font-family: 'icon-font';
      font-size: 1.2em;
      color: $input-error-color;
    }
  }
}
</style>
